import React, { useState, useRef, useEffect } from "react"
import { Offcanvas, CloseButton, Button, OverlayTrigger, Tooltip, Form, Modal, Tabs, Tab, Table, Nav } from 'react-bootstrap'
import { getFirestore, collection, getDocs, doc, setDoc, updateDoc, getDoc, deleteDoc, query, where, addDoc, writeBatch } from 'firebase/firestore'
import { FaCrown, FaUserTag, FaEnvelope, FaCalendarAlt, FaTrashAlt, FaMoneyBillWave, FaCogs, FaUser, FaMapMarkerAlt, FaLock, FaHistory, FaUsers, FaTools, FaCloudUploadAlt, FaShieldAlt } from 'react-icons/fa';
import QRCode from 'react-qr-code';
import { toast } from 'react-toastify'
import { convertDate, spinner, makeid } from "../utils/Helper"
import ChangePasswordButton from "../entry/ChangePasswordButton";
import apc from '../images/user.png'
import './xDocList.css';

// --- DATA TO BE LOADED INTO FIRESTORE ---
const membershipTable = {
    "Life": { cost: 900, occurrence: "One-time payment" },
    "Artist": { cost: 5, occurrence: "Monthly" },
    "Cancelled per Member Request": { cost: 0, occurrence: "Unlimited" },
    "Terminated - Suspended": { cost: 0, occurrence: "Unlimited" },
    "Deceased": { cost: 0, occurrence: "Unlimited" },
    "Journalist": { cost: 30, occurrence: "Annually" },
    "Journalist (Late Renewal)": { cost: 50, occurrence: "Annually" },
    "Journalist with Spouse": { cost: 55, occurrence: "Annually" },
    "Journalist with Spouse (Late Renewal)": { cost: 75, occurrence: "Annually" },
    "Communicator": { cost: 40, occurrence: "Annually" },
    "Communicator (Late Renewal)": { cost: 60, occurrence: "Annually" },
    "Communicator with Spouse": { cost: 65, occurrence: "Annually" },
    "Communicator with Spouse (Late Renewal)": { cost: 85, occurrence: "Annually" },
    "Social": { cost: 90, occurrence: "Annually" },
    "Social (Late Renewal)": { cost: 110, occurrence: "Annually" },
    "Social with Spouse": { cost: 115, occurrence: "Annually" },
    "Social with Spouse (Late Renewal)": { cost: 135, occurrence: "Annually" },
    "Service Industry": { cost: 60, occurrence: "Annually" },
    "Service Industry (Late Renewal)": { cost: 80, occurrence: "Annually" },
    "Service Industry with Spouse": { cost: 85, occurrence: "Annually" },
    "Service Industry with Spouse (Late Renewal)": { cost: 105, occurrence: "Annually" },
    "First Responder": { cost: 60, occurrence: "Annually" },
    "First Responder (Late Renewal)": { cost: 80, occurrence: "Annually" },
    "First Responder with Spouse": { cost: 85, occurrence: "Annually" },
    "First Responder with Spouse (Late Renewal)": { cost: 105, occurrence: "Annually" },
    "Retired": { cost: 50, occurrence: "Annually" },
    "Retired (Late Renewal)": { cost: 70, occurrence: "Annually" },
    "Retired with Spouse": { cost: 75, occurrence: "Annually" },
    "Retired with Spouse (Late Renewal)": { cost: 95, occurrence: "Annually" },
    "Honorary": { cost: 5, occurrence: "Annually" },
    "Employee": { cost: 5, occurrence: "Annually" },
    "Vendor": { cost: 5, occurrence: "Annually" },
    "Corporate - Journalist": { cost: 120, occurrence: "Annually" },
    "Corporate - Communicator": { cost: 160, occurrence: "Annually" },
    "Corporate - Social": { cost: 360, occurrence: "Annually" },
    "Pending Classification Correction or Reinstatement": { cost: 0, occurrence: "Unlimited" }
};

// --- COMPONENT: MEMBERSHIP MANAGER ---
const MembershipManager = ({ theme }) => {
    const isDark = theme === 'dark';
    const db = getFirestore();
    const [levels, setLevels] = useState({});
    const [newLevel, setNewLevel] = useState({ id: '', cost: '', occurrence: 'Annually' });

    const fetchLevels = async () => {
        const snap = await getDocs(collection(db, "membership_levels"));
        const data = {};
        snap.forEach(doc => data[doc.id] = doc.data());
        setLevels(data);
    };

    useEffect(() => { fetchLevels(); }, []);

    const populateMembershipLevels = async () => {
        if (!window.confirm("Synchronize with the master table?")) return;
        spinner(true);
        const batch = writeBatch(db);
        Object.entries(membershipTable).forEach(([level, data]) => {
            const docRef = doc(db, "membership_levels", level);
            batch.set(docRef, data);
        });
        try {
            await batch.commit();
            toast.success("Database Synchronized!");
            fetchLevels();
        } catch (e) { toast.error("Sync failed"); }
        spinner(false);
    };

    const handleSave = async (id, cost, occurrence) => {
        spinner(true);
        try {
            await setDoc(doc(db, "membership_levels", id), { cost: Number(cost), occurrence });
            toast.success("Level Updated");
            fetchLevels();
        } catch (e) { toast.error("Error saving level"); }
        spinner(false);
    };

    const handleDelete = async (id) => {
        if (window.confirm(`Delete ${id}?`)) {
            await deleteDoc(doc(db, "membership_levels", id));
            fetchLevels();
        }
    };

    return (
        <div className="p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="m-0 text-info"><FaCogs className="me-2"/> Membership Rates</h5>
                <Button size="sm" variant="outline-info" onClick={populateMembershipLevels}>
                    <FaCloudUploadAlt className="me-1" /> Sync Rates
                </Button>
            </div>
            <Table responsive variant={isDark ? "dark" : "light"} className="small" style={{ tableLayout: 'fixed', width: '100%' }}>
                <thead>
                    <tr>
                        <th style={{ width: '40%' }}>Level</th>
                        <th style={{ width: '22%', minWidth: '70px' }}>Cost ($)</th>
                        <th style={{ width: '28%' }}>Occurrence</th>
                        <th style={{ width: '10%', textAlign: 'center' }}></th>
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(levels).map(([id, data]) => (
                        <tr key={id} className="align-middle">
                            <td style={{ wordWrap: 'break-word', whiteSpace: 'normal', lineHeight: '1.2' }}>{id}</td>
                            <td><Form.Control size="sm" type="number" defaultValue={data.cost} onBlur={(e) => handleSave(id, e.target.value, data.occurrence)} className={isDark ? "glass-input px-1" : "px-1"} /></td>
                            <td>
                                <Form.Select size="sm" defaultValue={data.occurrence} onChange={(e) => handleSave(id, data.cost, e.target.value)} className={isDark ? "glass-input px-1" : "px-1"} style={{fontSize: '11px'}}>
                                    <option value="Monthly">Monthly</option>
                                    <option value="Annually">Annually</option>
                                    <option value="One-time payment">1-Time</option>
                                    <option value="Unlimited">Unlimited</option>
                                </Form.Select>
                            </td>
                            <td className="text-center"><Button variant="link" className="text-danger p-0" onClick={() => handleDelete(id)}><FaTrashAlt /></Button></td>
                        </tr>
                    ))}
                    <tr className="bg-info bg-opacity-10">
                        <td><Form.Control size="sm" placeholder="New..." value={newLevel.id} onChange={e => setNewLevel({...newLevel, id: e.target.value})} className={isDark ? "glass-input px-1" : "px-1"} /></td>
                        <td><Form.Control size="sm" type="number" placeholder="0" value={newLevel.cost} onChange={e => setNewLevel({...newLevel, cost: e.target.value})} className={isDark ? "glass-input px-1" : "px-1"} /></td>
                        <td>
                            <Form.Select size="sm" value={newLevel.occurrence} onChange={e => setNewLevel({...newLevel, occurrence: e.target.value})} className={isDark ? "glass-input px-1" : "px-1"} style={{fontSize: '11px'}}>
                                <option value="Monthly">Monthly</option>
                                <option value="Annually">Annually</option>
                                <option value="One-time payment">1-Time</option>
                            </Form.Select>
                        </td>
                        <td className="text-center"><Button size="sm" variant="info" className="p-0 w-100" style={{fontSize: '10px'}} onClick={() => { handleSave(newLevel.id, newLevel.cost, newLevel.occurrence); setNewLevel({id:'', cost:'', occurrence:'Annually'}); }}>Add</Button></td>
                    </tr>
                </tbody>
            </Table>
        </div>
    );
};

// --- SUB-COMPONENT: UPDATE PROFILE ---
export function UpdateProfile({ idx, filex, name, type, theme, onSendToParent, hide, isTriggerOnly = false }) {
    const isDark = theme === 'dark';
    const db = getFirestore();
    const fileInputRef = useRef(null);
    const [show, setShow] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [activeTab, setActiveTab] = useState('profile');
    const [historyLogs, setHistoryLogs] = useState([]);
    const [membershipRules, setMembershipRules] = useState({});

    const [formData, setFormData] = useState({
        name: '', dob: '', details: '', filex: '', type: '', email: '', exp: '', id: '', board: false, cell: '',
        title: '', member: false, otherPhone: '', address1: '', address2: '', city: '',
        state: '', zip: '', country: 'USA', company: '', jobTitle: '', paidThrough: '', serverPermit: '',
        sponsor: '', isAdmin: false
    });

    const fetchMembershipRules = async () => {
        const snap = await getDocs(collection(db, "membership_levels"));
        const rules = {};
        snap.forEach(doc => { rules[doc.id] = doc.data(); });
        setMembershipRules(rules);
    };

    const fetchHistory = async (id) => {
        const historyRef = collection(db, "profiles", id, "history");
        const snap = await getDocs(historyRef);
        const logs = snap.docs.map(d => d.data());
        setHistoryLogs(logs.sort((a, b) => b.timestamp?.seconds - a.timestamp?.seconds));
    };

    const handleOpen = async () => {
        if (!idx) return;
        spinner(true);
        try {
            await fetchMembershipRules();
            const docSnap = await getDoc(doc(db, "profiles", idx));
            if (docSnap.exists()) {
                const data = docSnap.data();
                setFormData({ ...data });
                setImagePreview(data.filex || apc);
                setActiveTab('profile');
                setShow(true);
                fetchHistory(idx);
            }
        } catch (err) { toast.error("Load failed"); }
        finally { spinner(false); }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        const emailToStore = formData.email.toLowerCase().trim();
        
        spinner(true);
        try {
            const docRef = doc(db, "profiles", formData.id);
            const docSnap = await getDoc(docRef);
            const oldData = docSnap.data();

            if (emailToStore !== oldData.email?.toLowerCase()) {
                const q = query(collection(db, "profiles"), where("email", "==", emailToStore));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    spinner(false);
                    return toast.error("This email is already in use by another member.");
                }
            }

            await updateDoc(docRef, { 
                ...formData, 
                email: emailToStore,
                filex: imagePreview === apc ? '' : imagePreview 
            });
            toast.success("Updated!");
            setShow(false);
            if (onSendToParent) onSendToParent(true);
        } catch (err) { toast.error(err.message); }
        finally { spinner(false); }
    };

    const renderFeeDisplay = () => {
        if (!formData.member || !formData.type || !membershipRules[formData.type]) return null;
        const info = membershipRules[formData.type];
        return (
            <div className={`mt-3 p-2 rounded border shadow-sm ${isDark ? 'bg-info bg-opacity-10 border-info text-info' : 'bg-info bg-opacity-10 border-info text-dark'}`}>
                <div className="d-flex align-items-center justify-content-center gap-2 small">
                    <FaMoneyBillWave /> <span className="fw-bold">Membership Fee</span>
                    <span className="badge bg-info text-dark ms-2">${info.cost} {info.occurrence}</span>
                </div>
            </div>
        );
    };

    const renderStatusBanner = () => {
        if (!hide) return null;
        return (
            <div className={`mt-3 p-3 rounded border ${isDark ? 'bg-secondary bg-opacity-10 border-secondary' : 'bg-light border-secondary opacity-75'}`}>
                <div className="row g-3 align-items-center text-center text-md-start">
                    {formData.board && (
                        <div className="col-md-4 border-md-end border-opacity-10">
                            <label className={`meta-label ${!isDark ? 'text-dark opacity-50' : ''}`}>Board Position</label>
                            <div className="small fw-bold text-warning"><FaCrown className="me-1" /> {formData.title}</div>
                        </div>
                    )}
                    <div className="col-md-4 border-md-end border-opacity-10">
                        <label className={`meta-label ${!isDark ? 'text-dark opacity-50' : ''}`}>Club Membership</label>
                        <div className="small fw-bold">{formData.member ? formData.type : 'Non-Member'}</div>
                    </div>
                    <div className="col-md-4">
                        <label className={`meta-label ${!isDark ? 'text-dark opacity-50' : ''}`}>Expiration Date</label>
                        <div className="small fw-bold text-danger"><FaCalendarAlt className="me-1" /> {formData.exp ? convertDate(formData.exp) : 'N/A'}</div>
                    </div>
                </div>
            </div>
        );
    };

    const modalStyles = {
        backdrop: isDark ? "bg-dark" : "bg-light opacity-50",
        content: isDark ? "bg-dark text-white shadow-lg" : "bg-light text-dark shadow-lg",
        input: isDark ? "glass-input" : "form-control border-light-subtle mb-3",
        nav: isDark ? 'profile-nav-dark' : 'profile-nav-light'
    };

    return (
        <>
            {!isTriggerOnly && (
                <Button onClick={handleOpen}>
                    <div className=" hoverX">
                        <img src={filex || apc} alt="User" className="thumb hoverX"  />
                    </div>
                    <span className="hoverX  d-block" > {name || "Profile"}</span>
                </Button>
            )}

            <Modal show={show} onHide={() => setShow(false)} centered size="lg" contentClassName={modalStyles.content} backdropClassName={modalStyles.backdrop}>
                <Modal.Header className={`border-bottom border-opacity-10 px-4 ${!isDark ? 'text-dark' : ''}`}>
                    <Modal.Title className="d-flex align-items-center gap-3">
                        <QRCode value={formData.email || "none"} size={45} />
                        <div><h4 className="m-0">{formData.name}</h4><div className="small opacity-50">ID: {formData.id}</div></div>
                    </Modal.Title>
                    <CloseButton variant={isDark ? "white" : undefined} onClick={() => setShow(false)} />
                </Modal.Header>
                <Modal.Body className="p-0">
                    <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
                        <div className="px-4 pt-3 border-bottom border-opacity-10">
                            <Nav variant="pills" className={`flex-column flex-md-row gap-2 mb-3 ${modalStyles.nav}`}>
                                <Nav.Item className="flex-fill"><Nav.Link eventKey="profile" className="text-center py-2"><FaUser className="me-2" /> General</Nav.Link></Nav.Item>
                                <Nav.Item className="flex-fill"><Nav.Link eventKey="address" className="text-center py-2"><FaMapMarkerAlt className="me-2" /> Address</Nav.Link></Nav.Item>
                                {!hide && (
                                    <Nav.Item className="flex-fill"><Nav.Link eventKey="staff" className="text-center py-2"><FaLock className="me-2" /> Staff Only</Nav.Link></Nav.Item>
                                )}
                                <Nav.Item className="flex-fill"><Nav.Link eventKey="history" className="text-center py-2"><FaHistory className="me-2" /> History</Nav.Link></Nav.Item>
                            </Nav>
                        </div>
                        <Tab.Content>
                            <Tab.Pane eventKey="profile">
                                <div className="p-4"><div className="row">
                                    <div className="col-md-4 text-center border-md-end border-opacity-10">
                                        <div className="mx-auto mb-3 rounded-circle overflow-hidden shadow" style={{width:'150px', height:'150px', border:'3px solid #ff8c00'}}><img src={imagePreview || apc} className="w-100 h-100" style={{objectFit:'cover'}} /></div>
                                        <div className="d-grid gap-2">
                                            <Button size="sm" variant="outline-info" onClick={() => fileInputRef.current.click()}>Change Photo</Button>
                                            <ChangePasswordButton theme={theme} />
                                        </div>
                                        <input type="file" className="d-none" ref={fileInputRef} onChange={(e) => {
                                            const reader = new FileReader();
                                            reader.onload = () => setImagePreview(reader.result);
                                            reader.readAsDataURL(e.target.files[0]);
                                        }} />
                                        {renderFeeDisplay()}
                                    </div>
                                    <div className="col-md-8 ps-md-4">
                                        <label className="meta-label">Full Name</label><input type="text" className={modalStyles.input} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                                        <label className="meta-label">Email Address (Primary Login ID)</label><input type="email" className={modalStyles.input} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} readOnly={hide} />
                                        <div className="row">
                                            <div className="col-6"><label className="meta-label">Cell Phone</label><input type="text" className={modalStyles.input} placeholder="123-456-7890" value={formData.cell} onChange={(e) => setFormData({ ...formData, cell: e.target.value })} /></div>
                                            <div className="col-6"><label className="meta-label">Date of Birth</label><input type="date" className={modalStyles.input} value={formData.dob} onChange={(e) => setFormData({ ...formData, dob: e.target.value })} /></div>
                                        </div>
                                        <label className="meta-label mt-2">Bio / Details</label><textarea className={modalStyles.input} rows="3" value={formData.details} onChange={(e) => setFormData({ ...formData, details: e.target.value })} />
                                    </div>
                                </div>
                                {renderStatusBanner()}
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="address">
                                <div className="p-4">
                                    <label className="meta-label">Street Address 1</label><input type="text" className={modalStyles.input} value={formData.address1} onChange={(e) => setFormData({ ...formData, address1: e.target.value })} />
                                    <label className="meta-label">Street Address 2</label><input type="text" className={modalStyles.input} value={formData.address2} onChange={(e) => setFormData({ ...formData, address2: e.target.value })} />
                                    <div className="row g-2">
                                        <div className="col-md-4"><label className="meta-label">City</label><input type="text" className={modalStyles.input} value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} /></div>
                                        <div className="col-md-4"><label className="meta-label">State</label><input type="text" className={modalStyles.input} value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} /></div>
                                        <div className="col-md-4"><label className="meta-label">Zip</label><input type="text" className={modalStyles.input} value={formData.zip} onChange={(e) => setFormData({...formData, zip: e.target.value})} /></div>
                                    </div>
                                    <hr className="my-3 opacity-10" />
                                    <div className="row">
                                        <div className="col-md-6"><label className="meta-label">Company / Employer</label><input type="text" className={modalStyles.input} value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} /></div>
                                        <div className="col-md-6"><label className="meta-label">Job Title</label><input type="text" className={modalStyles.input} value={formData.jobTitle} onChange={(e) => setFormData({...formData, jobTitle: e.target.value})} /></div>
                                    </div>
                                </div>
                            </Tab.Pane>
                            {!hide && (
                                <Tab.Pane eventKey="staff">
                                    <div className="p-4">
                                        <div className={`p-4 rounded border ${isDark ? 'bg-secondary bg-opacity-10 border-info' : 'bg-light border-info'}`}>
                                            
                                            {/* SYSTEM PERMISSIONS SECTION */}
                                            <div className="mb-4 border-bottom border-info border-opacity-10 pb-3">
                                                <label className="meta-label text-info d-flex align-items-center gap-2 mb-2">
                                                    <FaShieldAlt /> System Permissions
                                                </label>
                                                <Form.Check 
                                                    type="switch" 
                                                    id="admin-switch"
                                                    label="Admin Access (Dashboard & Directory)" 
                                                    checked={formData.isAdmin} 
                                                    onChange={(e) => setFormData({ ...formData, isAdmin: e.target.checked })} 
                                                    className="fw-bold text-danger"
                                                />
                                            </div>

                                            <div className="row g-3 mb-4">
                                                <div className="col-md-6"><label className="meta-label text-info">Membership Expires</label><input type="date" className={modalStyles.input} value={formData.exp} onChange={(e) => setFormData({ ...formData, exp: e.target.value })} /></div>
                                                <div className="col-md-6"><label className="meta-label text-info">Paid Through</label><input type="date" className={modalStyles.input} value={formData.paidThrough} onChange={(e) => setFormData({ ...formData, paidThrough: e.target.value })} /></div>
                                            </div>
                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <Form.Check type="switch" label="Board Member" checked={formData.board} onChange={(e) => setFormData({ ...formData, board: e.target.checked })} />
                                                    {formData.board && (
                                                        <Form.Select className={modalStyles.input + " mt-2"} value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}>
                                                            <option value="">-- Choose Title --</option>
                                                            <option value="President">President</option>
                                                            <option value="Vice-President">Vice-President</option>
                                                            <option value="Secretary">Secretary</option>
                                                            <option value="Treasurer">Treasurer</option>
                                                            <option value="Director">Director</option>
                                                        </Form.Select>
                                                    )}
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="meta-label text-info">Server Permit #</label>
                                                    <input type="text" className={modalStyles.input} value={formData.serverPermit} onChange={(e) => setFormData({ ...formData, serverPermit: e.target.value })} />
                                                </div>
                                            </div>
                                            <Form.Check type="switch" label="Club Member" checked={formData.member} onChange={(e) => setFormData({ ...formData, member: e.target.checked })} />
                                            {formData.member && (
                                                <div className="mt-2 ms-4 border-start ps-3 border-info border-opacity-25">
                                                    <Form.Select className={modalStyles.input} value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                                                        <option value="">-- Type --</option>
                                                        {Object.keys(membershipRules).map(t => <option key={t} value={t}>{t}</option>)}
                                                    </Form.Select>
                                                    <label className="meta-label mt-2">Sponsor</label>
                                                    <input type="text" className={modalStyles.input} value={formData.sponsor} onChange={(e) => setFormData({...formData, sponsor: e.target.value})} />
                                                    {renderFeeDisplay()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Tab.Pane>
                            )}
                            <Tab.Pane eventKey="history">
                                <div className="p-4 custom-scrollbar" style={{maxHeight:'350px', overflowY:'auto'}}>
                                    {historyLogs.map((log, i) => (
                                        <div key={i} className={`mb-2 p-3 rounded border ${isDark ? 'bg-white bg-opacity-5' : 'bg-secondary bg-opacity-10 shadow-sm'}`}>
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span className="text-info fw-bold">{log.updatedBy}</span>
                                                <span className="opacity-50 text-end">{log.timestamp?.toDate ? convertDate(log.timestamp.toDate()) : 'Recent'}</span>
                                            </div>
                                            <div className="small opacity-75">{log.changes}</div>
                                        </div>
                                    ))}
                                </div>
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                    <div className="p-4 border-top border-opacity-10 d-flex gap-2">
                        <Button variant="secondary" className="px-4" onClick={() => setShow(false)}>Cancel</Button>
                        <Button onClick={handleUpdate} variant="info" className="flex-grow-1 text-white fw-bold">Save All Changes</Button>
                    </div>
                </Modal.Body>
            </Modal>

            <style>{`
                .profile-nav-dark .nav-link { color: rgba(255,255,255,0.6); background: rgba(255,255,255,0.05); border-radius: 8px; margin: 0 2px; }
                .profile-nav-dark .nav-link.active { background-color: #00d4ff !important; color: #000 !important; font-weight: bold; }
                
                .profile-nav-light .nav-link { color: rgba(0,0,0,0.6); background: rgba(0,0,0,0.05); border-radius: 8px; margin: 0 2px; }
                .profile-nav-light .nav-link.active { background-color: #00d4ff !important; color: #fff !important; font-weight: bold; box-shadow: 0 4px 10px rgba(0,212,255,0.2); }
                
                .meta-label { font-size: 10px; text-transform: uppercase; opacity: 0.6; display: block; margin-bottom: 2px; }
            `}</style>
        </>
    );
}

// --- SUB-COMPONENT: CREATE PROFILE ---
const CreateProfile = ({ theme, onSendToParent }) => {
    const isDark = theme === 'dark';
    const db = getFirestore();
    const [show, setShow] = useState(false);
    const [step, setStep] = useState(1);
    const [imagePreview, setImagePreview] = useState(apc);
    const [membershipRules, setMembershipRules] = useState({});

    const [formData, setFormData] = useState({
        name: '', dob: '', details: '', type: '', email: '', cell: '', otherPhone: '',
        address1: '', address2: '', city: '', state: '', zip: '', country: 'USA',
        company: '', jobTitle: '', board: false, title: '', member: false, exp: '', 
        paidThrough: '', serverPermit: '', sponsor: '', isAdmin: false // Forced false
    });

    useEffect(() => {
        if (show) {
            getDocs(collection(db, "membership_levels")).then(snap => {
                const rules = {};
                snap.forEach(doc => rules[doc.id] = doc.data());
                setMembershipRules(rules);
            });
        }
    }, [show, db]);

    const handleCreate = async (e) => {
        e.preventDefault();
        const emailToStore = formData.email.toLowerCase().trim();
        
        spinner(true);
        try {
            const q = query(collection(db, "profiles"), where("email", "==", emailToStore));
            const querySnapshot = await getDocs(q);
            if (!querySnapshot.empty) {
                spinner(false);
                return toast.error("This email is already registered.");
            }

            const newId = makeid(12);
            await setDoc(doc(db, "profiles", newId), { 
                ...formData, 
                id: newId, 
                email: emailToStore,
                isAdmin: false, // Explicitly forced false on save
                timestamp: new Date() 
            });
            toast.success("Created!");
            onSendToParent();
            setShow(false);
            setStep(1);
        } catch (err) { toast.error("Failed"); }
        finally { spinner(false); }
    };

    const modalStyles = {
        backdrop: isDark ? "bg-dark" : "bg-light",
        content: isDark ? "bg-dark text-white" : "bg-light text-dark shadow",
        input: isDark ? "glass-input" : "form-control border-light-subtle mb-3"
    };

    return (
        <>
            <button className="btn-glass-create" onClick={() => setShow(true)}>+ Add Member</button>
            <Modal show={show} onHide={() => setShow(false)} centered contentClassName={modalStyles.content}>
                <Modal.Header className={`border-bottom border-opacity-10 ${!isDark ? 'text-dark' : ''}`}>
                    <Modal.Title>New Member Profile</Modal.Title>
                    <CloseButton variant={isDark ? "white" : undefined} onClick={() => setShow(false)} />
                </Modal.Header>
                <Modal.Body className="p-4">
                    <div className="d-flex justify-content-center mb-4 gap-2">
                        {[1, 2, 3, 4].map(i => <div key={i} className={`step-dot ${step >= i ? 'active' : ''}`}></div>)}
                    </div>
                    <form onSubmit={handleCreate}>
                        {step === 1 && (
                            <div className="animated fadeIn">
                                
                                <Form.Check type="switch" label="Board Member" checked={formData.board} onChange={(e) => setFormData({ ...formData, board: e.target.checked })} className="mb-2" />
                                {formData.board && (
                                    <Form.Select className={modalStyles.input} value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })}>
                                        <option value="">-- Choose Title --</option>
                                        <option value="President">President</option>
                                        <option value="Vice-President">Vice-President</option>
                                        <option value="Secretary">Secretary</option>
                                        <option value="Treasurer">Treasurer</option>
                                        <option value="Director">Director</option>
                                    </Form.Select>
                                )}
                                <Form.Check type="switch" label="Club Member" checked={formData.member} onChange={(e) => setFormData({ ...formData, member: e.target.checked })} className="mt-2" />
                                {formData.member && (
                                    <div className="mt-2 ms-3 border-start ps-3 border-info border-opacity-25">
                                        <label className="meta-label">Membership Level</label>
                                        <Form.Select className={modalStyles.input} value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                                            <option value="">-- Choose Type --</option>
                                            {Object.keys(membershipRules).map(type => <option key={type} value={type}>{type}</option>)}
                                        </Form.Select>
                                        <div className="row g-2">
                                            <div className="col-6"><label className="meta-label">Expires</label><input type="date" className={modalStyles.input} value={formData.exp} onChange={(e) => setFormData({ ...formData, exp: e.target.value })} /></div>
                                            <div className="col-6"><label className="meta-label">Paid Through</label><input type="date" className={modalStyles.input} value={formData.paidThrough} onChange={(e) => setFormData({ ...formData, paidThrough: e.target.value })} /></div>
                                        </div>
                                    </div>
                                )}
                                <Button variant="info" className="w-100 text-white mt-3" onClick={() => setStep(2)}>Next</Button>
                            </div>
                        )}
                        {step === 2 && (
                            <div className="animated fadeIn">
                                <label className="meta-label">Full Name</label><input type="text" className={modalStyles.input} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                                <label className="meta-label">Email (Login ID)</label><input type="email" className={modalStyles.input} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                                <label className="meta-label">Cell Phone</label><input type="text" className={modalStyles.input} placeholder="123-456-7890" value={formData.cell} onChange={(e) => setFormData({ ...formData, cell: e.target.value })} />
                                <label className="meta-label">Date of Birth</label><input type="date" className={modalStyles.input} value={formData.dob} onChange={(e) => setFormData({ ...formData, dob: e.target.value })} />
                                <div className="d-flex gap-2 mt-3"><Button variant="secondary" className="w-50" onClick={() => setStep(1)}>Back</Button><Button variant="info" className="w-50 text-white" onClick={() => setStep(3)}>Next</Button></div>
                            </div>
                        )}
                        {step === 3 && (
                            <div className="animated fadeIn">
                                <label className="meta-label">Street Address 1</label><input type="text" className={modalStyles.input} value={formData.address1} onChange={(e) => setFormData({ ...formData, address1: e.target.value })} />
                                <label className="meta-label">Street Address 2</label><input type="text" className={modalStyles.input} value={formData.address2} onChange={(e) => setFormData({ ...formData, address2: e.target.value })} />
                                <div className="row g-2">
                                    <div className="col-md-4"><label className="meta-label">City</label><input type="text" className={modalStyles.input} value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} /></div>
                                    <div className="col-md-4"><label className="meta-label">State</label><input type="text" className={modalStyles.input} value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} /></div>
                                    <div className="col-4"><label className="meta-label">Zip</label><input type="text" className={modalStyles.input} value={formData.zip} onChange={(e) => setFormData({...formData, zip: e.target.value})} /></div>
                                </div>
                                <hr className="my-3 opacity-10" />
                                <label className="meta-label">Company / Employer</label><input type="text" className={modalStyles.input} value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} />
                                <label className="meta-label">Job Title</label><input type="text" className={modalStyles.input} value={formData.jobTitle} onChange={(e) => setFormData({...formData, jobTitle: e.target.value})} />
                                <div className="d-flex gap-2 mt-3"><Button variant="secondary" className="w-50" onClick={() => setStep(2)}>Back</Button><Button variant="info" className="w-50 text-white" onClick={() => setStep(4)}>Next</Button></div>
                            </div>
                        )}
                        {step === 4 && (
                            <div className="text-center animated fadeIn">
                                <div className="mx-auto mb-3 rounded-circle overflow-hidden shadow" style={{ width: '120px', height: '120px', border: '2px solid #00d4ff' }}>
                                    <img src={apc} className="w-100 h-100" style={{ objectFit: 'cover' }} />
                                </div>
                                <p className="small opacity-75">Click complete to finish member setup.</p>
                                <div className="d-flex gap-2"><Button variant="secondary" className="w-50" onClick={() => setStep(3)}>Back</Button><Button variant="info" className="w-50 text-white" type="submit">Complete Creation</Button></div>
                            </div>
                        )}
                    </form>
                </Modal.Body>
            </Modal>
        </>
    );
};

// --- MEMBER LIST ITEM ---
const XList = (props) => {
    const isDark = props.theme === 'dark';
    const db = getFirestore();
    const deleteProfile = async () => {
        if (window.confirm(`Delete ${props.name}?`)) {
            spinner(true);
            try {
                await deleteDoc(doc(db, "profiles", props.id));
                toast.success("Deleted.");
                props.sendToXMembers(true);
            } catch (err) { toast.error(err.message); }
            finally { spinner(false); }
        }
    };
    return (
        <div className="doc-card animated fadeIn mb-3 p-0 overflow-hidden position-relative"
            style={{ border: isDark ? '1px solid rgba(0, 212, 255, 0.15)' : '1px solid #dee2e6', background: isDark ? 'rgba(6, 6, 6, 0.03)' : '#fff' }}>
            <button onClick={deleteProfile} className="circle-btn centerCircle btn-delete shadow-sm" style={{ position: 'absolute', top: '10px', right: '55px', zIndex: 2 }}><FaTrashAlt size={10} /></button>
            <div className="p-3">
                <div className="d-flex justify-content-between align-items-start">
                    <div className="text-start flex-grow-1">
                        <h5 className="m-0 fw-bold" style={{ color: isDark ? '#00d4ff' : '#0056b3' }}>{props.name}</h5>
                        <div className="d-flex flex-wrap gap-2 mt-2">
                            {props.board && <span className="text-warning small d-flex align-items-center"><FaCrown className="me-1" /> {props.title}</span>}
                            <span className={isDark ? "text-white-50 small" : "text-muted small"}><FaUserTag className="me-1" /> {props.type}</span>
                            {/* Visual indicator for Admin in the list */}
                            {props.isAdmin && <span className="text-danger small d-flex align-items-center fw-bold"><FaShieldAlt className="me-1" /> Admin</span>}
                        </div>
                    </div>
                </div>
                <div className="mt-3 d-flex align-items-center justify-content-between">
                    <div><span className="badge rounded-pill bg-success bg-opacity-10 text-success border px-2" style={{ fontSize: '10px' }}>ACTIVE</span><span className={isDark ? "ms-2 text-white-50 small" : "ms-2 text-muted small"}><FaCalendarAlt className="me-1" size={10} /> {convertDate(props.exp)}</span></div>
                    <UpdateProfile theme={props.theme} idx={props.id} filex={props.filex} name={props.name} onSendToParent={() => props.sendToXMembers(true)} hide={false} />
                </div>
            </div>
            <div className="px-3 py-2 border-top border-opacity-10" style={{ background: isDark ? 'rgba(255,255,255,0.02)' : '#f8f9fa' }}>
                <div className={isDark ? "text-white-50" : "text-muted"} style={{ fontSize: '11px' }}><FaEnvelope className="me-2 text-info" /> {props.email}</div>
            </div>
        </div>
    );
};

// --- MAIN EXPORT ---
function XMembers(props) {
    const { theme = 'dark' } = props;
    const isDark = theme === 'dark';
    const [showCanvas, setShowCanvas] = useState(false);
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("members");

    const getMembers = () => {
        spinner(true);
        getDocs(collection(getFirestore(), "profiles")).then((snapshot) => {
            let rec = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
            setData(rec);
            spinner(false);
            setShowCanvas(true);
        }).catch(() => spinner(false));
    };

    return (
        <>
            <OverlayTrigger overlay={<Tooltip>Member Directory</Tooltip>}><Button variant={isDark ? "primary" : "outline-primary"} onClick={getMembers} className="hoverX shadow-sm"><span className="circle-btn centerCircle">💛</span> Directory</Button></OverlayTrigger>
            <Offcanvas show={showCanvas} onHide={() => setShowCanvas(false)} placement="end" backdropClassName="adminBackdrop" className={isDark ? "bg-dark" : "bg-light border-start"}>
                <Offcanvas.Header className="pb-3 border-bottom border-opacity-10">
                    <div className="w-100">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h3 className={isDark ? "modified xTitle m-0" : "text-dark fw-bold m-0"}>Directory</h3>
                            <div className="d-flex align-items-center gap-2">
                                <CreateProfile theme={theme} onSendToParent={getMembers} />
                                <CloseButton variant={isDark ? "white" : undefined} onClick={() => setShowCanvas(false)} />
                            </div>
                        </div>
                        <Tab.Container activeKey={activeTab} onSelect={k => setActiveTab(k)}>
                            <Nav variant="pills" className="flex-column flex-md-row gap-2 mb-3 profile-nav-dark">
                                <Nav.Item className="flex-fill"><Nav.Link eventKey="members" className="text-center py-2"><FaUsers className="me-2" /> Members</Nav.Link></Nav.Item>
                                <Nav.Item className="flex-fill"><Nav.Link eventKey="manage" className="text-center py-2"><FaTools className="me-2" /> Manage Rates</Nav.Link></Nav.Item>
                            </Nav>
                        </Tab.Container>
                    </div>
                </Offcanvas.Header>
                <Offcanvas.Body className="custom-scrollbar p-0">
                    {activeTab === "members" ? (
                        <div className="p-3">
                            <Form.Control type="search" placeholder="🔎 Search members..." className={isDark ? "glass-search mb-3" : "form-control mb-3"} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                            <ul className="doc-container p-0">
                                {data.filter(m => m.name?.toLowerCase().includes(searchTerm.toLowerCase())).map((member) => (
                                    <XList key={member.id} theme={theme} sendToXMembers={getMembers} {...member} hide={false} />
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <MembershipManager theme={theme} />
                    )}
                </Offcanvas.Body>
            </Offcanvas>

            <style>{`
                .profile-nav-dark .nav-link { color: rgba(255,255,255,0.6); background: rgba(255,255,255,0.05); border-radius: 8px; margin: 0 2px; }
                .profile-nav-dark .nav-link.active { background-color: #00d4ff !important; color: #000 !important; font-weight: bold; }
                .meta-label { font-size: 10px; text-transform: uppercase; opacity: 0.6; display: block; margin-bottom: 2px; }
            `}</style>
        </>
    );
}

export default XMembers;